import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  buildReportsCleanedExportUrl,
  buildReportsLiteExportUrl,
  buildUsersStatisticsExportUrl,
  downloadAdminExport,
} from "../../utils/adminExports";
import { getAuthToken } from "../../api/auth";
import "../styles/AdminPanel.css";

interface AdminPanelProps {
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = memo(({ onClose }) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingExport, setDownloadingExport] = useState<
    "users" | "reports" | "cleaned" | null
  >(null);

  const hasDateRange = Boolean(fromDate || toDate);
  const hasInvalidDateRange = Boolean(fromDate && toDate && fromDate > toDate);
  const exportRange = useMemo(
    () => ({ from: fromDate, to: toDate }),
    [fromDate, toDate]
  );
  const filenamePeriod = `${fromDate || "all"}_${toDate || "all"}`;

  const usersStatisticsUrl = useMemo(
    () => buildUsersStatisticsExportUrl(exportRange),
    [exportRange]
  );
  const reportsLiteUrl = useMemo(
    () => buildReportsLiteExportUrl(exportRange),
    [exportRange]
  );
  const reportsCleanedUrl = useMemo(
    () => buildReportsCleanedExportUrl(exportRange),
    [exportRange]
  );

  const periodLabel = hasDateRange
    ? `${fromDate || t("admin.exports.start")} - ${
        toDate || t("admin.exports.today")
      }`
    : t("admin.exports.wholePeriod");

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handlePanelClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleClearDates = useCallback(() => {
    setFromDate("");
    setToDate("");
    setDownloadError(null);
  }, []);

  const downloadExport = useCallback(
    async (
      kind: "users" | "reports" | "cleaned",
      url: string,
      fallbackFilename: string
    ) => {
      const token = getAuthToken();
      if (!token) {
        setDownloadError(t("auth.authRequired"));
        return;
      }

      setDownloadingExport(kind);
      setDownloadError(null);

      try {
        await downloadAdminExport({ url, token, fallbackFilename });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t("admin.exports.downloadError");
        setDownloadError(message);
      } finally {
        setDownloadingExport(null);
      }
    },
    [t]
  );

  const handleUsersStatisticsExport = useCallback(() => {
    if (hasInvalidDateRange) {
      return;
    }

    downloadExport(
      "users",
      usersStatisticsUrl,
      `Users_statistics_${filenamePeriod}.csv`
    );
  }, [
    downloadExport,
    filenamePeriod,
    hasInvalidDateRange,
    usersStatisticsUrl,
  ]);

  const handleReportsLiteExport = useCallback(() => {
    if (hasInvalidDateRange) {
      return;
    }

    downloadExport("reports", reportsLiteUrl, `Reports_export_${filenamePeriod}.csv`);
  }, [downloadExport, filenamePeriod, hasInvalidDateRange, reportsLiteUrl]);

  const handleReportsCleanedExport = useCallback(() => {
    if (hasInvalidDateRange) {
      return;
    }

    downloadExport(
      "cleaned",
      reportsCleanedUrl,
      `Cleaned_export_${filenamePeriod}.csv`
    );
  }, [downloadExport, filenamePeriod, hasInvalidDateRange, reportsCleanedUrl]);

  return (
    <div className="admin-panel-overlay" onClick={handleOverlayClick}>
      <section
        className="admin-panel"
        onClick={handlePanelClick}
        aria-label={t("admin.exports.title")}
      >
        <header className="admin-panel-header">
          <div>
            <h3>{t("admin.exports.title")}</h3>
            <span>{t("admin.exports.subtitle")}</span>
          </div>
          <button
            type="button"
            className="admin-panel-close-button"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
          >
            ×
          </button>
        </header>

        <div className="admin-panel-content">
          <section className="admin-export-section">
            <div className="admin-export-section-header">
              <h4>{t("admin.exports.period")}</h4>
              {hasDateRange && (
                <button
                  type="button"
                  className="admin-export-clear-button"
                  onClick={handleClearDates}
                >
                  {t("admin.exports.clearDates")}
                </button>
              )}
            </div>

            <div className="admin-export-date-grid">
              <label className="admin-export-field">
                <span>{t("admin.exports.from")}</span>
                <input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              </label>

              <label className="admin-export-field">
                <span>{t("admin.exports.to")}</span>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </label>
            </div>

            {hasInvalidDateRange && (
              <div className="admin-export-error">
                {t("admin.exports.invalidRange")}
              </div>
            )}
            {downloadError && (
              <div className="admin-export-error">{downloadError}</div>
            )}
          </section>

          <div className="admin-export-list">
            <article className="admin-export-card">
              <div className="admin-export-card-copy">
                <h4>{t("admin.exports.usersStatisticsTitle")}</h4>
                <span>{periodLabel}</span>
              </div>
              <button
                type="button"
                className="admin-export-download-button"
                onClick={handleUsersStatisticsExport}
                disabled={hasInvalidDateRange || downloadingExport !== null}
              >
                {downloadingExport === "users"
                  ? t("admin.exports.downloading")
                  : t("admin.exports.downloadCsv")}
              </button>
            </article>

            <article className="admin-export-card">
              <div className="admin-export-card-copy">
                <h4>{t("admin.exports.reportsLiteTitle")}</h4>
                <span>{periodLabel}</span>
              </div>
              <button
                type="button"
                className="admin-export-download-button secondary"
                onClick={handleReportsLiteExport}
                disabled={hasInvalidDateRange || downloadingExport !== null}
              >
                {downloadingExport === "reports"
                  ? t("admin.exports.downloading")
                  : t("admin.exports.downloadCsv")}
              </button>
            </article>

            <article className="admin-export-card">
              <div className="admin-export-card-copy">
                <h4>{t("admin.exports.cleanedTitle")}</h4>
                <span>{periodLabel}</span>
              </div>
              <button
                type="button"
                className="admin-export-download-button secondary"
                onClick={handleReportsCleanedExport}
                disabled={hasInvalidDateRange || downloadingExport !== null}
              >
                {downloadingExport === "cleaned"
                  ? t("admin.exports.downloading")
                  : t("admin.exports.downloadCsv")}
              </button>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
});

AdminPanel.displayName = "AdminPanel";

export default AdminPanel;
